/**
 * v3.18-1263 PAYMENT-007B — Reveal Card reason dialog.
 *
 * Blocks unmasking until the operator supplies a valid reason.
 * The reason is submitted to POST /clients/{id}/credit-card-auth/{rec}/reveal
 * where it becomes part of the immutable system_audit + activities record.
 */
import { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { ShieldAlert, Loader2 } from 'lucide-react';

const CANNED_REASONS = [
  'Customer requested verification',
  'Processing payment',
  'Customer on the phone',
  'Manager approval',
  'Other',
];

export const RevealCardReasonDialog = ({ open, onOpenChange, onConfirm, cardLast4, submitting }) => {
  const [selected, setSelected] = useState('');
  const [freeText, setFreeText] = useState('');

  useEffect(() => {
    if (open) {
      setSelected('');
      setFreeText('');
    }
  }, [open]);

  const finalReason = selected === 'Other' ? freeText.trim() : selected;
  const canSubmit = !!finalReason && !submitting;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!submitting) onOpenChange(v); }}>
      <DialogContent className="max-w-md" data-testid="reveal-reason-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-rose-800">
            <ShieldAlert className="h-4 w-4" />
            Confirm Reveal — •••• {cardLast4 || '----'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-xs text-slate-600">
            Revealing sensitive card data is <strong>audited</strong>. Your identity, timestamp,
            authorization ID, IP address, and the reason below will be recorded immutably in the
            system audit log and Activity Timeline.
          </p>

          <div>
            <Label>Reason for Reveal</Label>
            <div className="mt-1 space-y-1.5" data-testid="reveal-reason-options">
              {CANNED_REASONS.map((r) => (
                <label
                  key={r}
                  className="flex items-center gap-2 text-sm cursor-pointer text-slate-700"
                >
                  <input
                    type="radio"
                    name="reveal-reason"
                    value={r}
                    checked={selected === r}
                    onChange={() => setSelected(r)}
                    data-testid={`reveal-reason-${r.toLowerCase().replace(/\s+/g, '-')}`}
                  />
                  {r}
                </label>
              ))}
            </div>
          </div>

          {selected === 'Other' && (
            <div>
              <Label htmlFor="reveal-reason-other">Specify reason</Label>
              <Input
                id="reveal-reason-other"
                value={freeText}
                onChange={(e) => setFreeText(e.target.value)}
                placeholder="Enter reason for reveal…"
                autoFocus
                data-testid="reveal-reason-other-input"
              />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            data-testid="reveal-reason-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={() => onConfirm(finalReason)}
            disabled={!canSubmit}
            className="bg-rose-700 hover:bg-rose-800 text-white"
            data-testid="reveal-reason-confirm-btn"
          >
            {submitting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null}
            Reveal Card
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RevealCardReasonDialog;
